import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../../styles/dashboards/SuperAdminDashboard.css';
import api from '../../api/api';

/**
 * SuperAdmin Dashboard Component
 * Displays management interface for centers and system-wide settings
 */
const SuperAdminDashboard = ({ data }) => {
  const [centersData, setCentersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Format date to readable string
  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
  
  // Fetch data using the secure dedicated super admin endpoint
  useEffect(() => {
    const fetchSuperAdminData = async () => {
      try {
        setLoading(true);
        
        // Use the dedicated super admin endpoint for centers
        const centersResponse = await api.get('/superadmin/centers');
        
        if (centersResponse.data && centersResponse.data.success) {
          setCentersData(centersResponse.data.data);
          console.log('Centers fetched successfully from dedicated endpoint:', centersResponse.data.data.length);
        }
      } catch (err) {
        console.error('Error fetching super admin centers:', err);
        setError('Failed to load centers data');
      } finally {
        setLoading(false);
      }
    };

    // Call the fetch function
    fetchSuperAdminData();
  }, []);
  
  // Use our fetched data, fall back to props data if available, or empty arrays as last resort
  const centers = centersData.length > 0 ? centersData : (data?.centers?.data || []);
  const admins = data?.admins?.data || [];
  
  return (
    <div className="superadmin-dashboard">
      <div className="dashboard-header">
        <h1>System Administration</h1>
        <div className="action-buttons">
          <Link to="/centers/new" className="action-button primary">
            Add New Center
          </Link>
        </div>
      </div>
      
      {/* Display error message if there was an issue fetching data */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}
      
      {/* Display loading spinner while fetching data */}
      {loading && (
        <div className="loading-spinner-container">
          <div className="loading-spinner"></div>
          <p>Loading center data...</p>
        </div>
      )}
      
      {/* Stats Section */}
      <div className="stats-section">
        <div className="stats-card">
          <h3>Centers</h3>
          <div className="stat-value">{centers.length}</div>
          <div className="stat-label">Active centers</div>
        </div>
        
        <div className="stats-card">
          <h3>Admins</h3>
          <div className="stat-value">{admins.length}</div>
          <div className="stat-label">Center administrators</div>
        </div>
        
        <div className="stats-card">
          <h3>System</h3>
          <div className="stat-value">Active</div>
          <div className="stat-label">System status</div>
        </div>
      </div>
      
      {/* Centers Section */}
      <div className="dashboard-panel">
        <h2>Centers</h2>
        {centers.length > 0 ? (
          <div className="centers-list">
            {centers.map((center) => (
              <div className="center-card" key={center.id}>
                <div className="center-info">
                  <h3>{center.name}</h3>
                  <p className="center-code">Code: {center.centerCode}</p>
                  <p className="center-details">
                    {center.admin?.name || 'No admin'} • {center.location || 'No location'}
                  </p>
                  <p className="center-stats">
                    <span>{center.users?.length || 0} Users</span>
                    <span>{center.halaqas?.length || 0} Classes</span>
                  </p>
                </div>
                <div className="center-actions">
                  <Link 
                    to={`/center/edit/${center._id}`}
                    className="edit-btn"
                  >
                    Edit
                  </Link>
                  <Link 
                    to={`/center/details/${center._id}`}
                    className="view-details-btn"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No centers found</p>
            <Link to="/centers/new" className="action-button">
              Add First Center
            </Link>
          </div>
        )}
      </div>
      
      {/* Recent Activity */}
      <div className="dashboard-panel">
        <h2>System Administrators</h2>
        {admins.length > 0 ? (
          <div className="admins-list">
            {admins.map((admin) => (
              <div className="admin-item" key={admin.id}>
                <div className="admin-avatar">
                  {admin.name?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="admin-info">
                  <h3>{admin.name}</h3>
                  <p>{admin.email}</p>
                  <p>Center: {admin.center?.name || 'Not assigned'}</p>
                </div>
                <div className="admin-actions">
                  <Link 
                    to={`/admins/${admin.id}`}
                    className="view-details-btn"
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No administrators found</p>
          </div>
        )}
      </div>
      
      {/* Quick Links Section */}
      <div className="quick-links-section">
        <h2>Quick Actions</h2>
        <div className="quick-links-grid">
          <Link to="/centers/new" className="quick-link-card">
            <div className="quick-link-icon">🏢</div>
            <h3>Add Center</h3>
          </Link>
          
          <Link to="/admins/new" className="quick-link-card">
            <div className="quick-link-icon">👤</div>
            <h3>Add Admin</h3>
          </Link>
          
          <Link to="/settings/features" className="quick-link-card">
            <div className="quick-link-icon">🔧</div>
            <h3>Features</h3>
          </Link>
          
          <Link to="/settings/system" className="quick-link-card">
            <div className="quick-link-icon">⚙️</div>
            <h3>System Settings</h3>
          </Link>
          
          <Link to="/audit-logs" className="quick-link-card">
            <div className="quick-link-icon">📋</div>
            <h3>Audit Logs</h3>
          </Link>
          
          <Link to="/backups" className="quick-link-card">
            <div className="quick-link-icon">💾</div>
            <h3>Backups</h3>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminDashboard;